import React, { useLayoutEffect, useRef, useState } from "react";
import { CHART_COLORS, smoothPath } from "./dashboardFormat";

const HEIGHT = 236;
const TOP = 10;
const BASELINE = 200;
const TICKS = 4;
const AXIS_WIDTH = 34;
const MIN_SLOT = 46;
const EDGE = 8;

const niceMax = (value) => {
  if (value <= TICKS) return TICKS;
  const raw = value / TICKS;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw);
  return step * TICKS;
};

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const Axis = ({ max, color, align }) => (
  <svg
    width={AXIS_WIDTH}
    height={HEIGHT}
    aria-hidden="true"
    className="flex-shrink-0"
  >
    {Array.from({ length: TICKS + 1 }, (_, i) => i / TICKS).map((t) => (
      <text
        key={t}
        x={align === "end" ? AXIS_WIDTH - 6 : 6}
        y={BASELINE - t * (BASELINE - TOP) + 4}
        textAnchor={align}
        fontSize="11"
        fill={color}
      >
        {Math.round(t * max)}
      </text>
    ))}
  </svg>
);

// Axes stay put; the plot between them scrolls sideways when the bars need more room than the card has.
const FunnelChart = ({ buckets = [] }) => {
  const [viewportRef, viewportWidth] = useWidth();
  const isEmpty = buckets.every((b) => !b.projects && !b.proposals);
  // One shared scale: projects and proposals are both counts, so their heights compare directly.
  const max = niceMax(Math.max(0, ...buckets.map((b) => Math.max(b.projects, b.proposals))));
  const plotHeight = BASELINE - TOP;
  const plotWidth = Math.max(
    viewportWidth,
    buckets.length * MIN_SLOT + EDGE * 2,
  );
  const slot = buckets.length ? (plotWidth - EDGE * 2) / buckets.length : 0;
  const barWidth = Math.min(36, slot * 0.55);
  const cx = (i) => EDGE + i * slot + slot / 2;
  const points = buckets.map((b, i) => [
    cx(i),
    BASELINE - (b.proposals / max) * plotHeight,
  ]);
  const summary = buckets
    .map((b) => `${b.label}: ${b.projects} projects, ${b.proposals} proposals`)
    .join("; ");
  const scrolls = plotWidth > viewportWidth + 1;
  const [hiddenLeft, setHiddenLeft] = useState(false);
  const onScroll = (e) => setHiddenLeft(e.currentTarget.scrollLeft > 4);
  const showEarlier = () =>
    viewportRef.current?.scrollBy({
      left: -viewportWidth * 0.8,
      behavior: "smooth",
    });

  // Open on the latest dates; re-run once the viewport has been measured and the plot exists.
  useLayoutEffect(() => {
    if (!viewportRef.current || viewportWidth === 0) return;
    viewportRef.current.scrollLeft = viewportRef.current.scrollWidth;
    setHiddenLeft(viewportRef.current.scrollLeft > 4);
  }, [plotWidth, viewportWidth, buckets.length, viewportRef]);

  return (
    <div
      className="relative flex w-full mt-2.5"
      style={{ height: HEIGHT + (scrolls ? 10 : 0) }}
    >
      <Axis max={max} color="#8A7A91" align="end" />
      <div className="relative flex-1 min-w-0">
        {hiddenLeft && (
          <>
            <div className="absolute left-0 top-0 bottom-[10px] w-10 z-[1] pointer-events-none bg-gradient-to-r from-white to-transparent" />
            <button
              type="button"
              onClick={showEarlier}
              className="absolute left-1 top-1 z-[2] px-2 py-0.5 rounded-md border border-[#EDE6F0] bg-white text-[12px] font-medium text-[#73607B] hover:text-[#A04A97] hover:border-[#C95EBE]"
            >
              &lsaquo; Earlier
            </button>
          </>
        )}
        <div
          ref={viewportRef}
          onScroll={onScroll}
          className="h-full w-full overflow-x-auto overflow-y-hidden dash-scroll"
          tabIndex={scrolls ? 0 : undefined}
          aria-label={
            scrolls
              ? "Scroll the chart sideways to see earlier dates"
              : undefined
          }
        >
          {viewportWidth > 0 && (
            <svg
              width={plotWidth}
              height={HEIGHT}
              role="img"
              aria-label={summary}
              className="block"
            >
              {Array.from({ length: TICKS + 1 }, (_, i) => i / TICKS).map(
                (t) => (
                  <line
                    key={t}
                    x1="0"
                    x2={plotWidth}
                    y1={BASELINE - t * plotHeight}
                    y2={BASELINE - t * plotHeight}
                    stroke="#EFEBF2"
                    strokeWidth="1"
                  />
                ),
              )}
              {buckets.map((b, i) => {
                const h = (b.projects / max) * plotHeight;
                return (
                  <g key={b.start}>
                    <rect
                      x={cx(i) - barWidth / 2}
                      y={BASELINE - h}
                      width={barWidth}
                      height={h}
                      rx="4"
                      fill={CHART_COLORS.projects}
                      fillOpacity={b.in_period === false ? 0.55 : 1}
                      className="dash-bar"
                      style={{
                        animationDelay: `${150 + Math.min(i, 20) * 30}ms`,
                      }}
                    >
                      <title>{`${b.label}: ${b.projects} projects, ${b.proposals} proposals`}</title>
                    </rect>
                    <text
                      x={cx(i)}
                      y={BASELINE + 22}
                      textAnchor="middle"
                      fontSize="12"
                      fill="#73607B"
                    >
                      {b.label}
                    </text>
                  </g>
                );
              })}
              {points.length > 1 && (
                <path
                  d={smoothPath(points)}
                  fill="none"
                  stroke={CHART_COLORS.proposals}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  pathLength="1"
                  className="dash-draw"
                  style={{ animationDelay: "400ms" }}
                />
              )}
              {points.map(([x, y], i) => (
                <circle
                  key={buckets[i].start}
                  cx={x}
                  cy={y}
                  r="4"
                  fill="#FFFFFF"
                  stroke={CHART_COLORS.proposals}
                  strokeWidth="2"
                  className="dash-pop"
                  style={{ animationDelay: `${600 + Math.min(i, 20) * 30}ms` }}
                >
                  <title>{`${buckets[i].label}: ${buckets[i].proposals} proposals`}</title>
                </circle>
              ))}
            </svg>
          )}
        </div>
      </div>
      {isEmpty && viewportWidth > 0 && (
        <div className="absolute inset-x-0 top-[70px] flex justify-center pointer-events-none">
          <span className="text-[13px] text-[#73607B] bg-white/90 px-3 py-1.5 rounded-md">
            No projects or proposals in this period yet
          </span>
        </div>
      )}
    </div>
  );
};

export default FunnelChart;
